'use client';

import Image, { type StaticImageData } from 'next/image';
import React from 'react';
import type { SupportedLanguage } from '@/app/types';
import { cn } from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import MultiElementIcon from '../_assets/Multi.png';
import NonPeakModeIcon from '../_assets/Peak.png';
import HighQualityIcon from '../_assets/Quality.png';
import SmoothAnimationIcon from '../_assets/Smooth.png';
import UltraFastIcon from '../_assets/Ultra.png';

type Feature = {
  id: string;
  title: Record<SupportedLanguage, string>;
  icon: StaticImageData;
};

export function FeatureSection() {
  const locale = useLocale();

  return (
    <section className="w-full bg-black py-12 sm:py-6">
      <div className="mx-auto w-full max-w-[1192px] px-6 sm:px-4">
        <div className="flex justify-items-center sm:flex-wrap sm:justify-center">
          {features.map((feature, index) => (
            <React.Fragment key={feature.id}>
              <div className={cn('flex w-[20%] flex-col items-center text-center sm:w-[30%]', index > 2 && 'sm:mt-8')}>
                <div className="mb-3 h-16 w-20 text-center sm:mb-1">
                  <Image
                    src={feature.icon}
                    alt={feature.title[locale]}
                    width={64}
                    height={64}
                    className="mx-auto object-contain sm:h-12 sm:w-12"
                  />
                </div>

                <div className="mx-auto h-[40px] min-w-[110px] whitespace-pre-line text-center font-medium text-base text-white leading-5 sm:text-xs">
                  {feature.title[locale]}
                </div>
              </div>

              {index < features.length - 1 && (
                <div className={cn('flex items-center', index === 2 && 'sm:hidden')}>
                  <div className="h-[60px] w-[2px] bg-white/20" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

// 特性数据
const features: Feature[] = [
  {
    id: 'ultra_fast',
    icon: UltraFastIcon,
    title: {
      zh: '极快\n生成速度',
      en: 'Ultra-Fast\nGeneration',
      ja: '超高速で\n生成',
      de: 'Ultra­schnelle\nErzeugung',
      es: 'Generación\nultra rápida',
      fr: 'Génération\nUltra-Rapide',
      ko: '초고속\n생성',
      pt: 'Geração\nUltra-Rápida',
      ru: 'Сверхбыстрая\nгенерация',
    },
  },
  {
    id: 'multi_element',
    title: {
      zh: '多主体\n一致性',
      en: 'Multi-Reference\nConsistency',
      ja: '一貫性のある\n複数の参照',
      de: 'Mehrfach­referenz\nKonsistenz',
      es: 'Coherencia\ncon múltiples referencias',
      fr: 'Consistance\nMulti-Instance',
      ko: '여러 참조 사용\n일관성 유지',
      pt: 'Consistência com\nMúltiplas Referências',
      ru: 'Согласованность по\nнескольким референсам',
    },
    icon: MultiElementIcon,
  },
  {
    id: 'non_peak',
    title: {
      zh: '错峰模式\n免费生成',
      en: 'Off-Peak\nMode',
      ja: 'オフピーク\nモード',
      de: 'Nebenzeiten-\nModus',
      es: 'Modo de horas\nde menor actividad',
      fr: 'Mode\nHors Pointe',
      ko: '비피크\n모드',
      pt: 'Modo\nFora de Pico',
      ru: 'Режим непиковой\nнагрузки',
    },
    icon: NonPeakModeIcon,
  },
  {
    id: 'high_quality',
    title: {
      zh: '生成\n高清视频',
      en: 'High-Quality\nVideo Generation',
      ja: '高画質な\n映像',
      de: 'Hochwertige\nVideoerstellung',
      es: 'Generación de vídeos\nde alta calidad',
      fr: 'Génération de Vidéo\nHaute Qualité',
      ko: '고품질\n영상 생성',
      pt: 'Geração de Vídeo\nem Alta Qualidade',
      ru: 'Генерация видео\nв высоком качестве',
    },
    icon: HighQualityIcon,
  },
  {
    id: 'smooth_animation',
    title: {
      zh: '流畅\n动漫画质',
      en: 'Smooth\n2D Animation',
      ja: 'なめらかな\n2Dアニメ',
      de: 'Flüssige\n2D-Animation',
      es: 'Animación 2D\nfluida',
      fr: 'Animation 2D\nFluide',
      ko: '부드러운\n2D 애니메이션',
      pt: 'Animação\n2D Suave',
      ru: 'Плавная\n2D-анимация',
    },
    icon: SmoothAnimationIcon,
  },
];
